import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import Toast from '../../components/UI/Toast';
import DataUpload from '../../components/DataUpload';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    CalendarIcon,
    UserIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    EyeIcon,
    TrashIcon,
    ArrowPathIcon,
    CloudArrowUpIcon,
    UserGroupIcon
} from '@heroicons/react/24/outline';

interface Task {
    _id: string;
    title: string;
    description: string;
    status: string;
    priority: string;
    dueDate: string;
    estimatedHours?: number;
    actualHours?: number;
    assignedBy?: {
        _id: string;
        firstName: string;
        lastName: string;
    };
    assignedTo?: {
        _id: string;
        firstName: string;
        lastName: string;
        designation?: string;
    };
    deliverables: Array<{
        description: string;
        isCompleted: boolean;
    }>;
    compensation: {
        amount: number;
        currency: string;
    };
    createdAt: string;
}

interface User {
    _id: string;
    firstName: string;
    lastName: string;
    designation?: string;
    email: string;
}

const CompanyTasks: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
    const [teamMembers, setTeamMembers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDataUploadModal, setShowDataUploadModal] = useState(false);
    const [selectedTaskForUpload, setSelectedTaskForUpload] = useState<string | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [refreshing, setRefreshing] = useState(false);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [assigneeFilter, setAssigneeFilter] = useState('all');

    // New task form
    const [newTask, setNewTask] = useState({
        title: '',
        description: '',
        status: 'pending',
        priority: 'medium',
        dueDate: '',
        estimatedHours: '',
        assignedTo: '',
        experience: 'mid-level',
        location: 'Remote',
        jobType: 'contract',
        compensation: {
            amount: '',
            currency: 'INR'
        },
        deliverables: [{ description: '', isCompleted: false }]
    });

    const fetchTasks = useCallback(async () => {
        try {
            // Company user's ID can be accessed as either id or _id
            const companyId = (user as any)?.id || (user as any)?._id;
            if (!companyId) {
                setError('Company ID not found');
                return;
            }
            const response = await api.get(`/companies/${companyId}/tasks`);
            setTasks(response.data.data || []);
            setError(null);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to fetch tasks';
            setError(errorMessage);
            dispatch(showToast({ message: errorMessage, type: 'error' }));
            console.error('Error fetching tasks:', err);
        }
    }, [user, dispatch]);

    const fetchTeamMembers = useCallback(async () => {
        try {
            // Company user's ID can be accessed as either id or _id
            const companyId = (user as any)?.id || (user as any)?._id;
            if (!companyId) {
                console.error('Company ID not found for team members');
                return;
            }
            const response = await api.get(`/companies/${companyId}/users`);
            setTeamMembers(response.data.data || []);
        } catch (err: any) {
            console.error('Error fetching team members:', err);
        }
    }, [user]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([fetchTasks(), fetchTeamMembers()]);
        setRefreshing(false);
    };

    const openDataUploadModal = (taskId: string) => {
        setSelectedTaskForUpload(taskId);
        setShowDataUploadModal(true);
    };

    const closeDataUploadModal = () => {
        setShowDataUploadModal(false);
        setSelectedTaskForUpload(null);
    };

    const handleDataUploadSuccess = () => {
        dispatch(showToast({
            message: 'Data uploaded successfully',
            type: 'success'
        }));
        closeDataUploadModal();
        fetchTasks(); // Refresh tasks to show updated data
    };

    useEffect(() => {
        if (user) {
            setLoading(true);
            Promise.all([fetchTasks(), fetchTeamMembers()]).finally(() => setLoading(false));
        }
    }, [user, fetchTasks, fetchTeamMembers]);

    // Apply filters
    useEffect(() => {
        let filtered = tasks.filter(task => {
            const matchesSearch = searchTerm === '' ||
                task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (task.assignedTo?.firstName + ' ' + task.assignedTo?.lastName).toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
            const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
            const matchesAssignee = assigneeFilter === 'all' || task.assignedTo?._id === assigneeFilter;

            return matchesSearch && matchesStatus && matchesPriority && matchesAssignee;
        });
        setFilteredTasks(filtered);
    }, [tasks, searchTerm, statusFilter, priorityFilter, assigneeFilter]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'in-progress':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'completed':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'overdue':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'urgent':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'high':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
            case 'medium':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'low':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const calculateCompletionPercentage = (task: Task) => {
        if (!task.deliverables || task.deliverables.length === 0) return 0;
        const completed = task.deliverables.filter(d => d.isCompleted).length;
        return Math.round((completed / task.deliverables.length * 100) || 0);
    };

    const formatCurrency = (amount: number, currency: string = 'INR') => {
        return `${currency} ${amount.toLocaleString()}`;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleCreateTask = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            // Company user's ID can be accessed as either id or _id
            const companyId = (user as any)?.id || (user as any)?._id;
            if (!companyId) {
                setError('Company ID not found');
                return;
            }

            const taskData = {
                title: newTask.title,
                description: newTask.description,
                status: newTask.status,
                priority: newTask.priority,
                dueDate: newTask.dueDate,
                estimatedHours: parseInt(newTask.estimatedHours),
                assignedTo: newTask.assignedTo || null,
                experience: newTask.experience,
                location: newTask.location,
                jobType: newTask.jobType,
                salary: {
                    amount: parseInt(newTask.compensation.amount),
                    currency: newTask.compensation.currency
                },
                compensation: {
                    amount: parseInt(newTask.compensation.amount),
                    currency: newTask.compensation.currency
                },
                deliverables: newTask.deliverables.filter(d => d.description.trim() !== ''),
                requirements: [], // Could be derived from form later
                company: companyId,
                postedBy: companyId,
                isPublic: false // Internal task
            };

            const response = await api.post('/jobs', taskData);
            if (response.data.success) {
                await fetchTasks(); // Refresh tasks
                setShowCreateModal(false);
                resetForm();
                dispatch(showToast({ message: 'Task created successfully', type: 'success' }));
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to create task';
            setError(errorMessage);
            dispatch(showToast({ message: errorMessage, type: 'error' }));
        }
    };

    const handleViewTask = (task: Task) => {
        setSelectedTask(task);
        setShowDetailsModal(true);
    };

    const resetForm = () => {
        setNewTask({
            title: '',
            description: '',
            status: 'pending',
            priority: 'medium',
            dueDate: '',
            estimatedHours: '',
            assignedTo: '',
            experience: 'mid-level',
            location: 'Remote',
            jobType: 'contract',
            compensation: {
                amount: '',
                currency: 'INR'
            },
            deliverables: [{ description: '', isCompleted: false }]
        });
    };

    const addDeliverable = () => {
        setNewTask(prev => ({
            ...prev,
            deliverables: [...prev.deliverables, { description: '', isCompleted: false }]
        }));
    };

    const removeDeliverable = (index: number) => {
        setNewTask(prev => ({
            ...prev,
            deliverables: prev.deliverables.filter((_, i) => i !== index)
        }));
    };

    const updateDeliverable = (index: number, description: string) => {
        setNewTask(prev => ({
            ...prev,
            deliverables: prev.deliverables.map((deliverable, i) =>
                i === index ? { ...deliverable, description } : deliverable
            )
        }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
                <h3 className="{{`text-lg font-medium text-red-800 dark:text-red-200`}}">Error</h3>
                <p className="{{`text-red-600 dark:text-red-400 mt-2`}}">{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="bg-white/10 rounded-lg p-3">
                            <CheckCircleIcon className="h-8 w-8" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Task Management</h1>
                            <p className="text-blue-100 mt-1">
                                Assign and track tasks for your team members
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="inline-flex items-center px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" />
                            Create Task
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <div className="bg-blue-100 dark:bg-blue-900 rounded-lg p-3">
                            <CheckCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Tasks</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{tasks.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <div className="bg-yellow-100 dark:bg-yellow-900 rounded-lg p-3">
                            <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {tasks.filter(t => t.status === 'pending').length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <div className="bg-green-100 dark:bg-green-900 rounded-lg p-3">
                            <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {tasks.filter(t => t.status === 'completed').length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <div className="bg-red-100 dark:bg-red-900 rounded-lg p-3">
                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Overdue</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {tasks.filter(t => new Date(t.dueDate) < new Date() && t.status !== 'completed').length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search tasks..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="overdue">Overdue</option>
                    </select>
                    <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="all">All Priorities</option>
                        <option value="urgent">Urgent</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>
                    <select
                        value={assigneeFilter}
                        onChange={(e) => setAssigneeFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="all">All Assignees</option>
                        {teamMembers.map(member => (
                            <option key={member._id} value={member._id}>
                                {member.firstName} {member.lastName}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tasks List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Tasks ({filteredTasks.length})
                        </h3>
                    </div>

                    {filteredTasks.length === 0 ? (
                        <div className="text-center py-8">
                            <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No tasks found</h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {tasks.length === 0
                                    ? "Get started by creating your first task."
                                    : "Try adjusting your search or filter criteria."
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredTasks.map((task) => (
                                <div key={task._id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    {task.title}
                                                </h4>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                                                    {task.status.toUpperCase()}
                                                </span>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                                                    {task.priority.toUpperCase()}
                                                </span>
                                            </div>
                                            <p className="text-gray-600 dark:text-gray-400 mb-3">
                                                {task.description || 'No description provided'}
                                            </p>
                                            <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                                                <div className="flex items-center">
                                                    <CalendarIcon className="h-4 w-4 mr-1" />
                                                    Due: {formatDate(task.dueDate)}
                                                </div>
                                                <div className="flex items-center">
                                                    <UserIcon className="h-4 w-4 mr-1" />
                                                    {task.assignedTo ? `${task.assignedTo.firstName} ${task.assignedTo.lastName}` : 'Unassigned'}
                                                </div>
                                                <div className="flex items-center">
                                                    <ClockIcon className="h-4 w-4 mr-1" />
                                                    {task.estimatedHours}h estimated
                                                </div>
                                                <div className="text-green-600 dark:text-green-400 font-medium">
                                                    {formatCurrency(task.compensation.amount, task.compensation.currency)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2 ml-4">
                                            <div className="text-right mr-4">
                                                <div className="flex items-center mb-1">
                                                    <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                                                        <div
                                                            className="bg-blue-500 h-2 rounded-full"
                                                            style={{ width: `${calculateCompletionPercentage(task)}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {calculateCompletionPercentage(task)}%
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {task.deliverables?.length || 0} deliverables
                                                </p>
                                            </div>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleViewTask(task)}
                                                    className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                                                    title="View Task"
                                                >
                                                    <EyeIcon className="h-5 w-5" />
                                                </button>
                                                <button
                                                    onClick={() => window.open(`/company/tasks/${task._id}`, '_blank')}
                                                    className="p-2 text-purple-600 hover:bg-purple-100 dark:hover:bg-purple-900 rounded-lg transition-colors"
                                                    title="View Applications"
                                                >
                                                    <UserGroupIcon className="h-5 w-5" />
                                                </button>
                                                <button
                                                    onClick={() => openDataUploadModal(task._id)}
                                                    className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900 rounded-lg transition-colors"
                                                    title="Upload Reference Data"
                                                >
                                                    <CloudArrowUpIcon className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Task Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Create New Task
                            </h2>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleCreateTask} className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Task Title *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={newTask.title}
                                        onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Due Date *
                                    </label>
                                    <input
                                        type="date"
                                        required
                                        value={newTask.dueDate}
                                        onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={newTask.description}
                                    onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Priority
                                    </label>
                                    <select
                                        value={newTask.priority}
                                        onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Assign To
                                    </label>
                                    <select
                                        value={newTask.assignedTo}
                                        onChange={(e) => setNewTask(prev => ({ ...prev, assignedTo: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Select team member</option>
                                        {teamMembers.map(member => (
                                            <option key={member._id} value={member._id}>
                                                {member.firstName} {member.lastName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Estimated Hours
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={newTask.estimatedHours}
                                        onChange={(e) => setNewTask(prev => ({ ...prev, estimatedHours: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Experience Level
                                    </label>
                                    <select
                                        value={newTask.experience}
                                        onChange={(e) => setNewTask(prev => ({ ...prev, experience: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="entry-level">Entry Level</option>
                                        <option value="mid-level">Mid Level</option>
                                        <option value="senior-level">Senior Level</option>
                                        <option value="expert">Expert</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Location
                                    </label>
                                    <input
                                        type="text"
                                        value={newTask.location}
                                        onChange={(e) => setNewTask(prev => ({ ...prev, location: e.target.value }))}
                                        placeholder="e.g., Remote, Mumbai, Bangalore"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Job Type
                                    </label>
                                    <select
                                        value={newTask.jobType}
                                        onChange={(e) => setNewTask(prev => ({ ...prev, jobType: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="full-time">Full Time</option>
                                        <option value="part-time">Part Time</option>
                                        <option value="contract">Contract</option>
                                        <option value="freelance">Freelance</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Compensation Amount
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        required
                                        value={newTask.compensation.amount}
                                        onChange={(e) => setNewTask(prev => ({
                                            ...prev,
                                            compensation: { ...prev.compensation, amount: e.target.value }
                                        }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Currency
                                    </label>
                                    <select
                                        value={newTask.compensation.currency}
                                        onChange={(e) => setNewTask(prev => ({
                                            ...prev,
                                            compensation: { ...prev.compensation, currency: e.target.value }
                                        }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="INR">INR</option>
                                        <option value="USD">USD</option>
                                        <option value="EUR">EUR</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Deliverables
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addDeliverable}
                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                    >
                                        + Add Deliverable
                                    </button>
                                </div>
                                {newTask.deliverables.map((deliverable, index) => (
                                    <div key={index} className="flex items-center space-x-2 mb-2">
                                        <input
                                            type="text"
                                            value={deliverable.description}
                                            onChange={(e) => updateDeliverable(index, e.target.value)}
                                            placeholder="Enter deliverable description"
                                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeDeliverable(index)}
                                            className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                >
                                    Create Task
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Task Details Modal */}
            {showDetailsModal && selectedTask && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {selectedTask.title}
                            </h2>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Status</p>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedTask.status)}`}>
                                        {(selectedTask.status).toUpperCase()}
                                    </span>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Priority</p>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedTask.priority)}`}>
                                        {(selectedTask.priority).toUpperCase()}
                                    </span>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Compensation</p>
                                    <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                                        {formatCurrency(selectedTask.compensation.amount, selectedTask.compensation.currency)}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Task Details</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Description</p>
                                            <p className="text-gray-900 dark:text-white">
                                                {selectedTask.description || 'No description provided'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Due Date</p>
                                            <p className="text-gray-900 dark:text-white">
                                                {formatDate(selectedTask.dueDate)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Estimated Hours</p>
                                            <p className="text-gray-900 dark:text-white">
                                                {selectedTask.estimatedHours || 'Not specified'} hours
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Assigned To</p>
                                            <p className="text-gray-900 dark:text-white">
                                                {selectedTask.assignedTo ?
                                                    `${selectedTask.assignedTo.firstName} ${selectedTask.assignedTo.lastName}` :
                                                    'Unassigned'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                        Progress ({calculateCompletionPercentage(selectedTask)}%)
                                    </h3>
                                    <div className="space-y-3">
                                        {selectedTask.deliverables && selectedTask.deliverables.length > 0 ? (
                                            <>
                                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                    <div
                                                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                                        style={{ width: `${calculateCompletionPercentage(selectedTask)}%` }}
                                                    ></div>
                                                </div>
                                                <div className="space-y-2">
                                                    {selectedTask.deliverables.map((deliverable, index) => (
                                                        <div key={index} className="flex items-center space-x-2">
                                                            <div className={`w-4 h-4 rounded-full ${deliverable.isCompleted ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                                                            <span className={`text-sm ${deliverable.isCompleted ? 'text-green-600 dark:text-green-400 line-through' : 'text-gray-700 dark:text-gray-300'}`}>
                                                                {deliverable.description}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        ) : (
                                            <p className="text-gray-500 dark:text-gray-400">No deliverables defined</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {selectedTask.assignedBy && (
                                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Task Assigner</h3>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        {selectedTask.assignedBy.firstName} {selectedTask.assignedBy.lastName}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Data Upload Modal */}
            {showDataUploadModal && selectedTaskForUpload && (
                <DataUpload
                    taskId={selectedTaskForUpload}
                    onUploadSuccess={handleDataUploadSuccess}
                    onClose={closeDataUploadModal}
                />
            )}

            <Toast />
        </div>
    );
};

export default CompanyTasks;